import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import {
  Button,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
  Slide,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Pagination,
  Box,
  Checkbox,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Sync as SyncIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  AudioFile as AudioIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import AnseraLogo from "assets/images/Anser.png";

import { useDomain } from "context/DomainContext";
import { FAST_API_BASE_URL } from "constants";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { enUS } from "date-fns/locale";
import { format } from "date-fns";
import { useAuth } from "context/AuthContext";
import { useSystemTheme } from "context/SystemThemeContext";

const ExternalMedia = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  const apiUrl = FAST_API_BASE_URL;
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;

  const [mediaTitle, setMediaTitle] = useState("");
  const [mediaLink, setMediaLink] = useState("");
  const [mediaData, setMediaData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isTitleupdated,setIsTitleupdated]=useState(false);

  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [search, setSearch] = useState("");

  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "desc",
  });

  const [selectedRows, setSelectedRows] = useState([]);
  const [editedTitles, setEditedTitles] = useState({});
  const [syncing, setSyncing] = useState(false);

  const [unsyncDialogOpen, setUnsyncDialogOpen] = useState(false);
  const [mediaToUnsync, setMediaToUnsync] = useState(null);
  const [unsyncing, setUnsyncing] = useState(false);

  const isFirstLoading = useRef(true);

  const fetchMedia = useCallback(async () => {
    if (!domainId) return;
    if (isFirstLoading.current) setLoading(true);

    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/sync-status`, {
        headers: {
          "DOMAIN-TOKEN": domainId,
          "Content-Type": "application/json",
        },
      });

      if (res.data?.result?.result) {
        const { media = {} } = res.data.result.result;
        const { videos = [], documents = [],external_web=[] } = media;

        const formatted = [
          ...videos.map((item) => ({
            id: item.video_id,
            title: item.video_title,
            link: item.video_url,
            status: item.status,
            timestamp: item.last_synced,
            type: item.post_type,
          })),
          ...documents.map((item) => ({
            id: item.post_id,
            title: item.post_title,
            link: item.post_url,
            status: item.status,
            timestamp: item.last_synced,
            type: item.post_type,
          })),
         ...external_web.map((item) => ({
            id: item.post_id,
            title: item.post_title,
            link: item.post_url,
            status: item.status,
            timestamp: item.last_synced,
            type: item.post_type,
          })),
        ];

        const uniqueMedia = formatted.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.link === item.link)
        );
        setMediaData(uniqueMedia);
      }
    } catch (err) {
      console.error("Error fetching media:", err);
      setSnackbarMessage("Failed to fetch media!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      if (isFirstLoading.current) {
        setLoading(false);
        isFirstLoading.current = false;
      }
    }
  }, [domainId]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mediaTitle || !mediaLink) return;

    try {
      await axios.post(
        `${apiUrl}/api/media-content`,
        [{ page_title: mediaTitle, url: mediaLink, media_data: "",is_title_updated:isTitleupdated }],
        { headers: { "DOMAIN-TOKEN": domainId } }
      );

      setSnackbarMessage("Media link saved successfully!");
      window.dispatchEvent(new CustomEvent('syncStarted'));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      fetchMedia();
      setMediaTitle("");
      setMediaLink("");
    } catch (err) {
      console.error("Error saving media:", err);
      setSnackbarMessage("Failed to save media!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      let newDirection = "desc";
      if (prev.key === columnKey && prev.direction === "desc") {
        newDirection = "asc";
      }
      return { key: columnKey, direction: newDirection };
    });
  };

  const toggleRowSelection = (id, title) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );

    setEditedTitles((prev) => {
      if (!prev[id]) {
        return { ...prev, [id]: title };
      }
      return prev;
    });
  };

  const handleSyncSelected = async () => {
    setSyncing(true);
    try {
      const rowsToSync = mediaData.filter((row) => selectedRows.includes(row.id));

      await Promise.all(
        rowsToSync.map((row) =>
          axios.post(
            `${apiUrl}/api/media-content`,
            [{
              page_id: row.id,
              page_title: editedTitles[row.id] ?? row.title,
              url: row.link,
              type: row.type,
              media_data: "",
              is_title_updated: isTitleupdated[row.id] || false,
            }],
            { headers: { "DOMAIN-TOKEN": domainId } }
          )
        )
      );

      setSnackbarMessage("Selected media synced successfully!");
      window.dispatchEvent(new CustomEvent('syncStarted'));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setSelectedRows([]);
      setEditedTitles({});
      fetchMedia();
    } catch (err) {
      console.error("Error syncing media:", err);
      setSnackbarMessage("Failed to sync selected media!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSyncing(false);
    }
  };

  const handleUnsyncClick = (media) => {
    setMediaToUnsync(media);
    setUnsyncDialogOpen(true);
  };

  const handleUnsyncConfirm = async () => {
    if (!mediaToUnsync) return;

    setUnsyncing(true);
    try {
      await axios.delete(`${apiUrl}/api/web-content/${mediaToUnsync.id}`, {
        headers: {
          "DOMAIN-TOKEN": domainId,
          "Content-Type": "application/json",
        },
      });

      setSnackbarMessage("Media unsynced successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      
      fetchMedia();
    } catch (err) {
      console.error("Error unsyncing media:", err);
      setSnackbarMessage("Failed to unsync media!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setUnsyncing(false);
      setUnsyncDialogOpen(false);
      setMediaToUnsync(null);
    }
  };

  const handleUnsyncCancel = () => {
    setUnsyncDialogOpen(false);
    setMediaToUnsync(null);
  };

const filteredData = useMemo(() => {
  return mediaData.filter((row) => {
    const title = row.title?.toLowerCase() || "";
    const link = row.link?.toLowerCase() || "";
    const type = row.type?.toLowerCase() || "";
    const status = row.status?.toLowerCase() || "";
    const searchText = search.toLowerCase();

    return (
      title.includes(searchText) ||
      link.includes(searchText) ||
      type.includes(searchText) ||
      status.includes(searchText)
    );
  });
}, [mediaData, search]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key])
        return sortConfig.direction === "asc" ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key])
        return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const totalEntries = sortedData.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const start = (page - 1) * entriesPerPage + 1;
  const end = Math.min(page * entriesPerPage, totalEntries);

  const paginatedData = sortedData.slice(
    (page - 1) * entriesPerPage,
    page * entriesPerPage
  );

const getTypeIcon = (type) => {
  if (type?.toLowerCase().includes("video")) {
    return <VideoIcon fontSize="small" />;
  }

  if (type?.toLowerCase().includes("audio")) {
    return <AudioIcon fontSize="small" />;
  }

  if (type?.toLowerCase().includes("external-web") || type?.toLowerCase().includes("link")) {
    return <LinkIcon fontSize="small" />;
  }

  return <DocumentIcon fontSize="small" />;
};

  const getStatusColor = (status) => {
    return status === "synced"
      ? "success"
      : status === "unsynced"
      ? "error"
      : status === "error"
      ? "error"
      : "warning";
  };

  return (
    <SoftBox
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#fff",
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: "20px",
        boxShadow: isDarkMode 
          ? "0px 8px 24px rgba(0, 0, 0, 0.3)" 
          : "0px 8px 24px rgba(0, 0, 0, 0.08)",
        maxWidth: "100%",
        overflow: "hidden",
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            color: "#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      <Dialog
        open={unsyncDialogOpen}
        onClose={handleUnsyncCancel}
        aria-labelledby="unsync-dialog-title"
        aria-describedby="unsync-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
            borderRadius: "16px",
          }
        }}
      >
        <DialogTitle id="unsync-dialog-title" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
          Confirm Unsync
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="unsync-dialog-description" sx={{ color: isDarkMode ? "#a0aec0" : undefined }}>
            Are you sure you want to unsync "{mediaToUnsync?.title}"? 
            This action will remove the media from your synced content.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleUnsyncCancel}
            disabled={unsyncing}
            sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUnsyncConfirm}
            disabled={unsyncing}
            variant="contained"
            color="error"
            startIcon={unsyncing ? <CircularProgress size={16} color="teal" /> : <DeleteIcon />}
            sx={{
              backgroundColor: "#d32f2f",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
              color: "#fff",
            }}
          >
            {unsyncing ? "Unsyncing..." : "Unsync"}
          </Button>
        </DialogActions>
      </Dialog>

      <SoftBox mb={3}>
        <SoftBox display="flex" alignItems="center" gap={2} mb={2}>
          <img 
            src={AnseraLogo} 
            alt="Ansera Logo" 
            width={isMobile ? 48 : 64}
            style={{ 
              borderRadius: "8px",
              filter: isDarkMode ? "brightness(0) invert(1)" : "none",
              transition: "filter 0.3s ease",
            }}
          />
          <SoftBox>
            <SoftTypography 
              variant={isMobile ? "h5" : "h4"} 
              fontWeight="bold"
              sx={{
                background: "linear-gradient(135deg, #219297 0%, #1a767a 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              External Media
            </SoftTypography>
            <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
              Manage and sync your media library
            </SoftTypography>
          </SoftBox>
        </SoftBox>

        <Divider sx={{ 
          mb: 2, 
          borderColor: isDarkMode ? "#1a2542" : "#e5e7eb" 
        }} />

        <SoftTypography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }} mb={2}>
          Sync your external documents, videos, and audio files with Ansera.
          <br />
          <strong>Note:</strong> Documents and links must be publicly viewable to be synced
        </SoftTypography>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                background: isDarkMode 
                  ? "linear-gradient(135deg, #060C18 0%, #0a1020 100%)"
                  : "linear-gradient(135deg, #f8fffe 0%, #f0fffe 100%)",
                borderRadius: "12px",
                boxShadow: "none",
                border: `1px solid ${isDarkMode ? "#1a2542" : "#219197"}`,
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                  <AudioIcon sx={{ color: "#219297" }} />
                  <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#219297" }}>
                    Audio Formats
                  </SoftTypography>
                </SoftBox>
                <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
                  mp3, m4a, ogg, wav, wma
                </SoftTypography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                background: isDarkMode 
                  ? "linear-gradient(135deg, #060C18 0%, #0a1020 100%)"
                  : "linear-gradient(135deg, #f8fffe 0%, #f0fffe 100%)",
                borderRadius: "12px",
                boxShadow: "none",
                border: `1px solid ${isDarkMode ? "#1a2542" : "#219297"}`,
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                  <VideoIcon sx={{ color: "#219297" }} />
                  <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#219297" }}>
                    Video Formats
                  </SoftTypography>
                </SoftBox>
                <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
                  mp4, m4v, webm, wmv, ogv
                </SoftTypography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                background: isDarkMode 
                  ? "linear-gradient(135deg, #060C18 0%, #0a1020 100%)"
                  : "linear-gradient(135deg, #f8fffe 0%, #f0fffe 100%)",
                borderRadius: "12px",
                boxShadow: "none",
                border: `1px solid ${isDarkMode ? "#1a2542" : "#219297"}`,
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                  <DocumentIcon sx={{ color: "#219297" }} />
                  <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#219297" }}>
                    Document Formats
                  </SoftTypography>
                </SoftBox>
                <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
                  doc, docx, pdf, ppt, pptx, xls, xlsx
                </SoftTypography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: "16px",
          background: isDarkMode 
            ? "linear-gradient(135deg, #060C18 0%, #0a1020 100%)"
            : "linear-gradient(135deg, #f8fffe 0%, #f0fffe 100%)",
          border: `1px solid ${isDarkMode ? "#1a2542" : "#d1fae5"}`,
        }}
      >
        <SoftBox
          component="form"
          onSubmit={handleSubmit}
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <SoftTypography variant="button" fontWeight="bold" sx={{ color: isDarkMode ? "#e2e8f0" : "#1a202c" }}>
            Add New Media
          </SoftTypography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
<TextField
  placeholder="Enter media title"
  value={mediaTitle}
  onChange={(e) => {
                    setMediaTitle(e.target.value)
                    setIsTitleupdated(true)}
                    }
  fullWidth
  size="small"
  InputProps={{
    startAdornment: <DocumentIcon sx={{ mr: 1, color: isDarkMode ? '#a0aec0' : '#666' }} />,
  }}
  sx={{
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDarkMode ? '#060C18 !important' : '#fff',
      '& fieldset': {
        borderColor: isDarkMode ? '#4a5568 !important' : '#d2d6da',
      },
      '&:hover fieldset': {
        borderColor: '#219297 !important',
      },
    },
    '& .MuiInputBase-input': {
      color: isDarkMode ? '#e2e8f0 !important' : '#344767',
    },
    '& .MuiInputBase-input::placeholder': {
      color: isDarkMode ? '#a0aec0 !important' : '#9e9e9e',
      opacity: 1,
    },
  }}
/>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
  placeholder="Enter media URL"
  value={mediaLink}
  onChange={(e) => setMediaLink(e.target.value)}
  fullWidth
  size="small"
  InputProps={{
    startAdornment: <LinkIcon sx={{ mr: 1, color: isDarkMode ? '#a0aec0' : '#666' }} />,
  }}
  sx={{
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDarkMode ? '#060C18 !important' : '#fff',
      '& fieldset': {
        borderColor: isDarkMode ? '#4a5568 !important' : '#d2d6da',
      },
      '&:hover fieldset': {
        borderColor: '#219297 !important',
      },
    },
    '& .MuiInputBase-input': {
      color: isDarkMode ? '#e2e8f0 !important' : '#344767',
    },
        '& .MuiInputBase-input::placeholder': {
      color: isDarkMode ? '#a0aec0 !important' : '#9e9e9e',
      opacity: 1,
    },
  }}
/>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: "#219297",
                  color: "#fff",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: "12px",
                  height: "100%",
                  minHeight: "44px",
                  boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                  "&:hover": {
                    backgroundColor: "#1a767a",
                    boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                  },
                }}
              >
                Save Media
              </Button>
            </Grid>
          </Grid>
        </SoftBox>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: `1px solid ${isDarkMode ? "#1a2542" : "#e5e7eb"}`,
          overflow: "hidden",
          backgroundColor: isDarkMode ? "#060C18" : "#fff",
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Box textAlign="center">
              <CircularProgress sx={{ color: "#219297" }} size={48} />
              <SoftTypography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }} mt={2}>
                Loading media...
              </SoftTypography>
            </Box>
          </Box>
        ) : (
          <>
            <SoftBox
              p={{ xs: 2, sm: 3 }}
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={2}
              sx={{ backgroundColor: isDarkMode ? "#0a1020" : "#f9fafb" }}
            >
              <SoftBox display="flex" alignItems="center" gap={1}>
                <SoftSelect
                  value={{ value: entriesPerPage, label: entriesPerPage }}
                  options={[5, 10, 15, 20, 25, 50].map((e) => ({
                    value: e,
                    label: e,
                  }))}
                  onChange={({ value }) => {
                    setEntriesPerPage(value);
                    setPage(1);
                  }}
                  size="small"
                />
                <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "secondary" }}>
                  entries per page
                </SoftTypography>
              </SoftBox>

              <SoftBox sx={{ width: { xs: "100%", sm: "280px" } }}>
                <TextField
                  placeholder="Search media..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: isDarkMode ? '#a0aec0' : '#666' }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: isDarkMode ? '#060C18 !important' : '#fff',
                      '& fieldset': {
                        borderColor: isDarkMode ? '#4a5568 !important' : '#d2d6da',
                      },
                      '&:hover fieldset': {
                        borderColor: '#219297 !important',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: isDarkMode ? '#e2e8f0 !important' : '#344767',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: isDarkMode ? '#a0aec0 !important' : '#9e9e9e',
                      opacity: 1,
                    },
                  }}
                />
              </SoftBox>
            </SoftBox>

            <Divider sx={{ borderColor: isDarkMode ? "#1a2542" : "#e5e7eb" }} />

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: isMobile ? "100%" : "800px" }}>
                <thead>
                  <TableRow sx={{ backgroundColor: isDarkMode ? "#0a1020" : "#f9fafb" }}>
                    {mediaData.some((row) => row.status === "unsynced" || row.status === "error") && (
                      <DataTableHeadCell align="center" width="60px">
                        <Checkbox
                          size="small"
                          indeterminate={
                            selectedRows.length > 0 &&
                            selectedRows.length <
                              mediaData.filter((r) => r.status === "unsynced" || r.status === "error").length
                          }
                          checked={
                            selectedRows.length > 0 &&
                            selectedRows.length ===
                              mediaData.filter((r) => r.status === "unsynced" || r.status === "error").length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              const toSync = mediaData.filter((r) => r.status === "unsynced" || r.status === "error");
                              setSelectedRows(toSync.map((r) => r.id));
                              setEditedTitles((prev) => {
                                const updated = { ...prev };
                                toSync.forEach((r) => {
                                  if (!updated[r.id]) updated[r.id] = r.title;
                                });
                                return updated;
                              });
                            } else {
                              setSelectedRows([]);
                              setEditedTitles({});
                            }
                          }}
                          sx={{
                            color: "#219297",
                            "&.Mui-checked": { color: "#219297" },
                          }}
                        />
                      </DataTableHeadCell>
                    )}
                    {[
                      { accessor: "link", Header: "Media Link" },
                      { accessor: "title", Header: "Title" },
                      { accessor: "type", Header: "Type" },
                      { accessor: "status", Header: "Status" },
                      { accessor: "timestamp", Header: "Last Synced" },
                      { accessor: "actions", Header: "Actions" },
                    ].map((col) => (
                      <DataTableHeadCell
                        key={col.accessor}
                        align="left"
                        onClick={() => col.accessor !== "actions" && handleSort(col.accessor)}
                        sorted={col.accessor !== "actions" && sortConfig.key === col.accessor ? sortConfig.direction : "none"}
                        showSortIcon={col.accessor !== "actions"}
                        style={{ 
                          cursor: col.accessor !== "actions" ? "pointer" : "default", 
                          userSelect: "none",
                          color: isDarkMode ? "#e2e8f0" : undefined,
                        }}
                      >
                        {col.Header}
                      </DataTableHeadCell>
                    ))}
                  </TableRow>
                </thead>

                <TableBody>
                  {paginatedData.map((row, index) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        "&:hover": { backgroundColor: isDarkMode ? "#060C18" : "#f9fafb" },
                        backgroundColor: index % 2 === 0 
                          ? (isDarkMode ? "#0a1020" : "#fff")
                          : (isDarkMode ? "#060C18" : "#fafafa"),
                      }}
                    >
                      {mediaData.some((r) => r.status === "unsynced" || r.status === "error") && (
                        <DataTableBodyCell align="center">
                          {(row.status === "unsynced" || row.status === "error") && (
                            <Checkbox
                              size="small"
                              checked={selectedRows.includes(row.id)}
                              onChange={() => toggleRowSelection(row.id, row.title)}
                              sx={{
                                color: "#219297",
                                "&.Mui-checked": { color: "#219297" },
                              }}
                            />
                          )}
                        </DataTableBodyCell>
                      )}

                      <DataTableBodyCell align="left">
                        <Tooltip title={row.link} placement="top">
                          <a
                            href={row.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#219297",
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              maxWidth: isMobile ? "150px" : "300px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontWeight: 500,
                            }}
                          >
                            <LinkIcon fontSize="small" />
                            {row.link}
                          </a>
                        </Tooltip>
                      </DataTableBodyCell>

                      <DataTableBodyCell align="left">
                        {(selectedRows.includes(row.id) && (row.status === "unsynced" || row.status === "error")) ? (
                          <SoftInput
                            value={editedTitles[row.id] ?? row.title}
                            onChange={(e) => {
                              setEditedTitles((prev) => ({
                                ...prev,
                                [row.id]: e.target.value,
                              }));
                            
                              setIsTitleupdated((prev) => ({
                                ...prev,
                                [row.id]: e.target.value !== row.title,
                              }));
                            }}
                            size="small"
                            sx={{ maxWidth: isMobile ? "150px" : "400px" }}
                          />
                        ) : (
                          <SoftTypography
                            variant="button"
                            fontWeight="regular"
                            sx={{
                              color: isDarkMode ? "#e2e8f0" : "#1a202c",
                              maxWidth: isMobile ? "150px" : "300px",
                              display: "inline-block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.title}
                          </SoftTypography>
                        )}
                      </DataTableBodyCell>

                      <DataTableBodyCell align="left">
                        <Chip
                          icon={getTypeIcon(row.type)}
                          label={row.type==="external-web" ? "Web page" : row.type}
                          size="small"
                          sx={{
                            backgroundColor: isDarkMode ? "#060C18" : "#f0fdf4",
                            color: isDarkMode ? "#07c44c" : "#166534",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            border: isDarkMode ? "1px solid #1a2542" : "none",
                          }}
                        />
                      </DataTableBodyCell>

                      <DataTableBodyCell align="left">
                        <Chip
                          label={row.status}
                          size="small"
                          color={getStatusColor(row.status)}
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                          }}
                        />
                      </DataTableBodyCell>

                      <DataTableBodyCell align="left">
                        <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
                          {row.timestamp ? format(new Date(row.timestamp), "MMM dd, yyyy HH:mm") : "-"}
                        </SoftTypography>
                      </DataTableBodyCell>
                      <DataTableBodyCell align="left">
                        {row.status === "synced" && (
                          <Tooltip title="Unsync media">
                            <IconButton
                              size="small"
                              onClick={() => handleUnsyncClick(row)}
                              sx={{
                                color: "#d32f2f",
                                "&:hover": {
                                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </DataTableBodyCell>
                    </TableRow>
                  ))}

                  {paginatedData.length === 0 && (
                    <TableRow>
                      <DataTableBodyCell
                        align="center"
                        colSpan={
                          mediaData.some((r) => r.status === "unsynced" || r.status === "error") ? 7 : 6
                        }
                      >
                        <Box py={4}>
                          <DocumentIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                          <SoftTypography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
                            No media found
                          </SoftTypography>
                        </Box>
                      </DataTableBodyCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <SoftBox
              p={{ xs: 2, sm: 3 }}
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={2}
              sx={{ 
                backgroundColor: isDarkMode ? "#0a1020" : "#f9fafb", 
                borderTop: `1px solid ${isDarkMode ? "#1a2542" : "#e5e7eb"}` 
              }}
            >
              <SoftTypography variant="button" sx={{ color: isDarkMode ? "#a0aec0" : "secondary" }}>
                Showing {totalEntries > 0 ? start : 0} to {end} of{" "}
                {new Intl.NumberFormat(enUS).format(totalEntries)} entries
              </SoftTypography>

              <SoftBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={2}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  shape="rounded"
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 600,
                      color: isDarkMode ? "#e2e8f0" : undefined,
                      "&.Mui-selected": {
                        backgroundColor: "#219297",
                        color: "white",
                        "&:hover": { backgroundColor: "#1a767a" },
                      },
                    },
                  }}
                />
                <SoftTypography variant="button" sx={{ color: isDarkMode ? "#a0aec0" : "secondary", alignSelf: "center" }}>
                  Page {page} of {totalPages || 1}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            
            {selectedRows.length > 0 && (
              <Box
                p={{ xs: 2, sm: 3 }}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: isDarkMode ? "#060C18" : "#ecfdfa",
                  borderTop: "2px solid #219297",
                }}
              >
                <SoftTypography variant="button" fontWeight="bold" sx={{ color: isDarkMode ? "#e2e8f0" : "#1a202c" }}>
                  {selectedRows.length} item{selectedRows.length > 1 ? "s" : ""} selected
                </SoftTypography>
                <Button
                  variant="contained"
                  disabled={syncing}
                  startIcon={syncing ? null : <SyncIcon />}
                  sx={{
                    backgroundColor: "#219297",
                    color: "#fff",
                    fontWeight: "bold",
                    textTransform: "none",
                    borderRadius: "12px",
                    px: 3,
                    py: 1,
                    boxShadow: "0 4px 12px rgba(33, 146, 151, 0.3)",
                    "&:hover": {
                      backgroundColor: "#1a767a",
                      boxShadow: "0 6px 16px rgba(33, 146, 151, 0.4)",
                    },
                    "&:disabled": {
                      backgroundColor: isDarkMode ? "#1a2542" : "#94a3b8",
                    },
                  }}
                  onClick={handleSyncSelected}
                >
                  {syncing ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    "Sync Selected"
                  )}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </SoftBox>
  );
};

export default ExternalMedia;